import type { AdvisorFeedback } from '@ppwr/convex/players/types'
import type { AdvisorSupportedModeValue } from '@ppwr/convex/players/constants'
import { ACTION_ADVISOR_FEEDBACK_GRACE_MS, ACTION_ADVISOR_STATUS } from './actionAdvisor.constants'
import { isSameActionAdvisorScope, type ActionAdvisorScope } from './useActionAdvisorStore'

type ActionAdvisorStatus = (typeof ACTION_ADVISOR_STATUS)[keyof typeof ACTION_ADVISOR_STATUS]

export function getActionAdvisorAvailability({
  configEnabled,
  enabled,
  feedbackEnabled,
  hasActor,
  mode,
  supportedModes
}: {
  configEnabled: boolean
  enabled: boolean
  feedbackEnabled: boolean
  hasActor: boolean
  mode?: string
  supportedModes: readonly AdvisorSupportedModeValue[]
}): {
  canRequestFeedback: boolean
  isSupportedMode: boolean
  shouldShowAdvisor: boolean
  shouldShowToggle: boolean
  status: ActionAdvisorStatus
} {
  if (!configEnabled) {
    return {
      status: ACTION_ADVISOR_STATUS.featureDisabled,
      shouldShowToggle: false,
      shouldShowAdvisor: false,
      isSupportedMode: false,
      canRequestFeedback: false
    }
  }

  if (!hasActor) {
    return {
      status: ACTION_ADVISOR_STATUS.missingActor,
      shouldShowToggle: false,
      shouldShowAdvisor: false,
      isSupportedMode: false,
      canRequestFeedback: false
    }
  }

  if (!mode) {
    return {
      status: ACTION_ADVISOR_STATUS.missingMode,
      shouldShowToggle: false,
      shouldShowAdvisor: false,
      isSupportedMode: false,
      canRequestFeedback: false
    }
  }

  const isSupportedMode = supportedModes.includes(mode as AdvisorSupportedModeValue)

  if (!isSupportedMode) {
    return {
      status: ACTION_ADVISOR_STATUS.unsupportedMode,
      shouldShowToggle: false,
      shouldShowAdvisor: false,
      isSupportedMode,
      canRequestFeedback: false
    }
  }

  // Context allows the advisor. The drawer "available" switch (`enabled`) gates
  // the avatar's visibility; the avatar tap (`feedbackEnabled`) gates feedback.
  if (!enabled) {
    return {
      status: ACTION_ADVISOR_STATUS.userDisabled,
      shouldShowToggle: true,
      shouldShowAdvisor: false,
      isSupportedMode,
      canRequestFeedback: false
    }
  }

  return {
    status: ACTION_ADVISOR_STATUS.available,
    shouldShowToggle: true,
    shouldShowAdvisor: true,
    isSupportedMode,
    canRequestFeedback: feedbackEnabled
  }
}

export function hasActionAdvisorActor(scope: Pick<ActionAdvisorScope, 'gameId' | 'userId'>): boolean {
  return Boolean(scope.gameId && scope.userId)
}

export function canSetActionAdvisorFeedback({
  enabled,
  feedbackEnabled,
  scope
}: {
  enabled: boolean
  feedbackEnabled: boolean
  scope: Pick<ActionAdvisorScope, 'gameId' | 'userId'>
}): boolean {
  return Boolean(enabled && feedbackEnabled && scope.gameId && scope.userId)
}

/** Which message owns the advisor bubble right now:
 * - pending turn-start advice outranks the last action grade (advice → action);
 * - a grade that followed shown advice ('confirmed') shows NO bubble — the
 *   avatar answers with a thumbs-up instead; the observation is still
 *   recorded for the end-of-hand recap;
 * - otherwise the latest action grade / outcome recap shows as-is. */
export function getDisplayedAdvisorFeedback({
  advice,
  adviceCurrent,
  feedback
}: {
  advice?: AdvisorFeedback
  adviceCurrent: boolean
  feedback?: AdvisorFeedback
}): AdvisorFeedback | undefined {
  if (adviceCurrent && advice) return advice
  if (feedback?.conversation?.kind === 'confirmed') return undefined
  return feedback
}

export function getScopedActionAdvisorFeedback({
  feedback,
  feedbackScope,
  feedbackUpdatedAt,
  scope,
  now = Date.now()
}: {
  feedback?: AdvisorFeedback
  feedbackScope?: ActionAdvisorScope
  feedbackUpdatedAt?: number
  scope: ActionAdvisorScope
  now?: number
}): AdvisorFeedback | undefined {
  if (!feedback) {
    return undefined
  }

  if (isSameActionAdvisorScope(feedbackScope, scope)) {
    return feedback
  }

  if (!(feedbackScope && feedbackUpdatedAt)) {
    return undefined
  }

  if (
    feedbackScope.gameId !== scope.gameId ||
    feedbackScope.userId !== scope.userId ||
    typeof feedbackScope.handNumber !== 'number' ||
    typeof scope.handNumber !== 'number'
  ) {
    return undefined
  }

  const isImmediateNextHand = scope.handNumber === feedbackScope.handNumber + 1
  const isFreshCarryover = now - feedbackUpdatedAt <= ACTION_ADVISOR_FEEDBACK_GRACE_MS

  return isImmediateNextHand && isFreshCarryover ? feedback : undefined
}
